import { useState } from "react";
import { toast } from "sonner";
import { Check, Sparkles, Loader2 } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { Plans, errText } from "../../lib/plans";

const TIERS = [
  { tier: "hobbyist", name: "Hobbyist", price: 15, credits: "200 Credits/mo", cta: "Try Hobbyist", accent: "cyan",
    features: ["200 credits every month", "Basic queue", "Up to 10 files per batch", "Auto-rename + EXIF on every file"] },
  { tier: "creator", name: "Creator", price: 29, credits: "600 Credits/mo + Batch Processing", cta: "Try Creator", accent: "purple", popular: true,
    features: ["600 credits every month", "Batch Processing up to 500 files", "Fast Queue", "Camera, Gallery & Desktop Watcher"] },
  { tier: "pro", name: "Pro Suite", price: 59, credits: "1,500 Credits/mo + Priority Speed", cta: "Try Pro", accent: "green",
    features: ["1,500 credits every month", "Priority Speed queue", "Batch Processing up to 500 files", "Aerial survey + KML/GeoJSON"] },
];

const ACCENT = {
  cyan: { ring: "border-cyan-500/30 hover:border-cyan-400/60", dot: "text-cyan-300", glow: "" },
  purple: { ring: "border-purple-500/60", dot: "text-purple-300", glow: "shadow-[0_0_40px_rgba(147,51,234,0.3)] lg:-translate-y-3" },
  green: { ring: "border-emerald-500/30 hover:border-emerald-400/60", dot: "text-emerald-300", glow: "" },
};

export function useStartTrial() {
  const { user, login } = useAuth();
  const [busy, setBusy] = useState(null);
  const start = async (tier) => {
    if (!user) return login(`/billing?try=${tier}`);
    setBusy(tier);
    try {
      const { url } = await Plans.checkout(tier);
      window.location.assign(url);
    } catch (e) {
      toast.error(errText(e, "Could not start checkout"));
      setBusy(null);
    }
  };
  return { start, busy };
}

export function PricingCards({ current, trialEligible = true }) {
  const { start, busy } = useStartTrial();
  return (
    <div className="grid gap-6 lg:grid-cols-3 items-stretch" data-testid="pricing-cards">
      {TIERS.map((t, i) => {
        const a = ACCENT[t.accent];
        const isCurrent = current === t.tier;
        return (
          <div key={t.tier} style={{ animationDelay: `${i * 90}ms` }}
            className={`suite-rise relative rounded-2xl border bg-slate-950/70 backdrop-blur-xl p-7 flex flex-col transition-[transform,border-color] ${a.ring} ${a.glow}`}
            data-testid={`pricing-card-${t.tier}`}>
            {t.popular && (
              <span className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full text-[11px] font-bold tracking-wider text-white bg-gradient-to-r from-purple-600 to-fuchsia-500 flex items-center gap-1" data-testid="pricing-popular-badge">
                <Sparkles size={12} /> MOST POPULAR
              </span>
            )}
            <p className={`font-mono text-xs tracking-[0.25em] uppercase ${a.dot}`}>{t.name}</p>
            <p className="mt-3 font-display text-5xl font-extrabold text-white">${t.price}<span className="text-base font-medium text-slate-400">/mo</span></p>
            <p className="mt-2 text-sm text-slate-300">{t.credits}</p>
            <ul className="mt-6 space-y-2.5 text-sm flex-1">
              {t.features.map((f) => (
                <li key={f} className="flex gap-2"><Check size={16} className={`${a.dot} shrink-0 mt-0.5`} /> <span>{f}</span></li>
              ))}
            </ul>
            <p className="mt-6 text-xs text-slate-500">{trialEligible ? "7-day free trial · 10 trial credits · card on file, no charge today" : "Your free trial has been used · billed monthly from today"}</p>
            <button onClick={() => start(t.tier)} disabled={!!busy || isCurrent} data-testid={`pricing-try-${t.tier}`}
              className={`mt-4 w-full py-3 rounded-full font-semibold transition-[transform,box-shadow] hover:-translate-y-0.5 disabled:opacity-50 disabled:hover:translate-y-0 ${t.popular ? "text-white bg-gradient-to-r from-cyan-500 via-purple-600 to-emerald-500 shadow-[0_0_24px_rgba(147,51,234,0.4)]" : "text-white border border-white/20 hover:border-white/50 bg-white/5"}`}>
              {busy === t.tier ? <Loader2 size={16} className="inline animate-spin" /> : isCurrent ? "Current plan" : trialEligible ? t.cta : `Subscribe to ${t.name}`}
            </button>
          </div>
        );
      })}
    </div>
  );
}
